/**
 * Starting and releasing a background processor outside a LiveKit track
 * (SC-158, SC-377).
 *
 * The pre-join preview drives a processor itself, and its step-down brings a
 * second one up beside the first. Both need the same three things, and they
 * were written out twice: starting `init()` with a bound, counting the frames
 * that come out, and giving the processor back. The call lifecycle needs the
 * last one too, for the processors it discards. Kept in one place so the rules
 * cannot drift apart.
 */
import { Track } from 'livekit-client'
import type { BackgroundProcessorWrapper } from '@livekit/track-processors'

import { PROCESSOR_START_TIMEOUT_MS, destroyBackgroundProcessor } from './liveKitVideoBackground'

/**
 * Destroy `processor` now, and once more when an `init()` that is still running
 * settles.
 *
 * The library's `destroy()` does nothing while `init()` runs, and an `init()`
 * that lands after an abort or a timeout starts the pipeline anyway: it would
 * segment its camera until that stream ends, and its segmenter and WASM memory
 * would never be closed. `initSettled` is that `init()`'s outcome, or null when
 * it is over.
 */
export function destroyProcessorAndLateInit(
  processor: BackgroundProcessorWrapper | null | undefined,
  initSettled?: Promise<void> | null
): void {
  void destroyBackgroundProcessor(processor)
  if (initSettled) void initSettled.then(() => destroyBackgroundProcessor(processor))
}

export interface StartedProcessor {
  processor: BackgroundProcessorWrapper
  /**
   * The hidden element the transformer pulls frames through. It is kept out of
   * the document: it is never displayed, and attaching it would risk layout and
   * autoplay quirks.
   */
  input: HTMLVideoElement
  /**
   * How `init()` ended: `started`, `timed-out` (after PROCESSOR_START_TIMEOUT_MS)
   * or `aborted`. Rejects with `init()`'s own error.
   */
  outcome: Promise<'started' | 'timed-out' | 'aborted'>
  /**
   * Give the camera track to the input element and play it. Only after `started`.
   * Rejects if playback is refused, because that yields no frames and a frozen
   * preview.
   */
  feed: () => Promise<void>
  /** Stop feeding, and destroy the processor, late `init()` included. Safe to repeat. */
  release: () => void
}

/**
 * Start `processor` on `sourceTrack`, bounded.
 *
 * Bounded and abortable because `init()` loads MediaPipe's script, WASM and
 * model with no timeout of its own, and a hung one would otherwise keep the
 * preview covered, and its WebGL context held, for good.
 */
export function startProcessorOnTrack(
  processor: BackgroundProcessorWrapper,
  sourceTrack: MediaStreamTrack,
  signal?: AbortSignal
): StartedProcessor {
  const input = document.createElement('video')
  input.muted = true
  input.playsInline = true

  const init = processor.init({ kind: Track.Kind.Video, track: sourceTrack, element: input })
  /** Settles with `init()`, whatever its outcome; null once it has. */
  let initPending: Promise<void> | null = null
  const pending = init.then(
    () => undefined,
    () => undefined
  )
  initPending = pending
  void pending.then(() => {
    if (initPending === pending) initPending = null
  })

  let startTimer: ReturnType<typeof setTimeout> | undefined
  const outcome = Promise.race([
    init.then(() => 'started' as const),
    new Promise<'timed-out'>((resolve) => {
      startTimer = setTimeout(() => resolve('timed-out'), PROCESSOR_START_TIMEOUT_MS)
    }),
    new Promise<'aborted'>((resolve) => {
      signal?.addEventListener('abort', () => resolve('aborted'), { once: true })
    }),
  ]).finally(() => clearTimeout(startTimer))
  // Whoever awaits `outcome` gets the rejection; this only keeps one that lands
  // before they do from surfacing as unhandled.
  void outcome.catch(() => undefined)

  return {
    processor,
    input,
    outcome,
    feed: async () => {
      input.srcObject = new MediaStream([sourceTrack])
      await input.play().catch(() => {
        // Autoplay refusal here yields no frames, so treat it as a failure to
        // attach rather than a silently frozen preview.
        throw new Error('preview input element could not play')
      })
    },
    release: () => {
      input.srcObject = null
      destroyProcessorAndLateInit(processor, initPending)
    },
  }
}

/** Frames out of one processor, and the means to wait for more. */
export interface FrameCounter {
  readonly seen: number
  /** Count a processed frame, and release the waiters it satisfies. */
  record: () => void
  /** Resolves true once `count` more frames have come, false after `timeoutMs`. */
  wait: (count: number, timeoutMs: number) => Promise<boolean>
}

export function createFrameCounter(): FrameCounter {
  let seen = 0
  const waiters: Array<{ target: number; resolve: (produced: boolean) => void }> = []
  return {
    get seen() {
      return seen
    },
    record: () => {
      seen += 1
      for (let i = waiters.length - 1; i >= 0; i -= 1) {
        if (waiters[i].target <= seen) waiters.splice(i, 1)[0].resolve(true)
      }
    },
    wait: (count, timeoutMs) =>
      new Promise<boolean>((resolve) => {
        const target = seen + count
        let settled = false
        const settle = (produced: boolean) => {
          if (settled) return
          settled = true
          clearTimeout(timer)
          resolve(produced)
        }
        const timer = setTimeout(() => settle(false), timeoutMs)
        waiters.push({ target, resolve: settle })
      }),
  }
}
